import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

export interface FileUploadEvent {
  file: File;
  preview?: string;
}

@Component({
  selector: 'app-file-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="upload-zone"
      [class.dragover]="isDragOver()"
      [class.has-file]="selectedFile()"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (click)="openFilePicker()"
      (keydown.enter)="openFilePicker()"
      (keydown.space)="openFilePicker()"
      tabindex="0"
      role="button"
      [attr.aria-label]="selectedFile() ? 'Change file: ' + selectedFile()?.name : 'Upload a file'">
      <input
        #fileInput
        type="file"
        class="visually-hidden"
        [accept]="accept()"
        (change)="onFileSelected($event)"
        [attr.aria-describedby]="'upload-hint-' + id" />

      @if (selectedFile(); as file) {
        <div class="selected-file">
          <span class="material-symbols-outlined file-icon" aria-hidden="true">
            description
          </span>
          <div class="file-info">
            <span class="file-name">{{ file.name }}</span>
            <span class="file-size">{{ formatFileSize(file.size) }}</span>
          </div>
          <button
            type="button"
            class="remove-btn"
            (click)="onRemove($event)"
            aria-label="Remove file">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
      } @else {
        <span class="material-symbols-outlined upload-icon" aria-hidden="true">
          cloud_upload
        </span>
        <p class="upload-text">
          <span class="upload-link">Click to upload</span> or drag and drop
        </p>
        <p class="upload-hint" [id]="'upload-hint-' + id">
          {{ hint() || 'Maximum file size: ' + formatFileSize(maxSize()) }}
        </p>
      }
    </div>

    @if (error()) {
      <p class="error-message" role="alert">{{ error() }}</p>
    }
  `,
  styles: `
    .upload-zone {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      border: 2px dashed oklch(0.8 0 0);
      border-radius: 0.75rem;
      background: oklch(0.98 0 0);
      cursor: pointer;
      transition: all 0.15s;
    }

    .upload-zone:hover,
    .upload-zone:focus-visible {
      border-color: oklch(0.6 0.1 250);
      background: oklch(0.97 0.02 250);
    }

    .upload-zone:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: 2px;
    }

    .upload-zone.dragover {
      border-color: oklch(0.5 0.2 250);
      background: oklch(0.95 0.03 250);
    }

    .upload-zone.has-file {
      border-style: solid;
      padding: 1rem;
    }

    .upload-icon {
      font-size: 2.5rem;
      color: oklch(0.6 0 0);
      margin-bottom: 0.75rem;
    }

    .upload-text {
      margin: 0;
      font-size: 0.875rem;
      color: oklch(0.4 0 0);
    }

    .upload-link {
      color: oklch(0.5 0.15 250);
      font-weight: 500;
    }

    .upload-hint {
      margin: 0.5rem 0 0;
      font-size: 0.75rem;
      color: oklch(0.55 0 0);
    }

    .selected-file {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
    }

    .file-icon {
      font-size: 1.5rem;
      color: oklch(0.5 0.1 250);
    }

    .file-info {
      flex: 1;
      min-width: 0;
    }

    .file-name {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      color: oklch(0.3 0 0);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-size {
      font-size: 0.75rem;
      color: oklch(0.55 0 0);
    }

    .remove-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: oklch(0.5 0 0);
      cursor: pointer;
      transition: all 0.15s;
    }

    .remove-btn:hover {
      background: oklch(0.95 0.05 25);
      color: oklch(0.5 0.15 25);
    }

    .error-message {
      margin: 0.5rem 0 0;
      font-size: 0.75rem;
      color: oklch(0.5 0.18 25);
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `,
})
export class FileUploadComponent {
  readonly accept = input<string>('*/*');
  readonly maxSize = input(10 * 1024 * 1024); // 10MB default
  readonly hint = input<string>();

  readonly fileSelected = output<FileUploadEvent>();
  readonly fileRemoved = output<void>();

  protected readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly selectedFile = signal<File | null>(null);
  protected readonly isDragOver = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly id = Math.random().toString(36).substring(2, 9);

  protected openFilePicker(): void {
    this.fileInput().nativeElement.click();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  private processFile(file: File): void {
    this.error.set(null);

    if (file.size > this.maxSize()) {
      this.error.set(`File size exceeds maximum of ${this.formatFileSize(this.maxSize())}`);
      return;
    }

    this.selectedFile.set(file);
    this.fileSelected.emit({ file });
  }

  protected onRemove(event: Event): void {
    event.stopPropagation();
    this.selectedFile.set(null);
    this.error.set(null);
    this.fileInput().nativeElement.value = '';
    this.fileRemoved.emit();
  }

  protected formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
